import { useEffect, useMemo, useRef, useState } from 'react';
import type { ZohoVendor } from '../../types';
import { searchVendors } from '../../lib/match-zoho-vendor';

interface Props {
  vendors: ZohoVendor[];
  value: string | null; // vendor.id sélectionné
  onChange: (vendorId: string | null) => void;
  /** Affiché si aucune sélection. */
  placeholder?: string;
  /**
   * Ce qu'on cherche, au singulier : « fournisseur » par défaut, « client »
   * pour les bénéficiaires de type client. Sert au message de liste vide, qui
   * annonçait « Aucun fournisseur trouvé » alors qu'on cherchait un client.
   */
  nomDeLObjet?: string;
  /**
   * Prévenu à chaque frappe, pour les listes qui se cherchent côté SERVEUR.
   * Indispensable quand le référentiel dépasse ce qu'on peut charger : les
   * clients Books sont plus de dix mille, et les envoyer tous au navigateur
   * revenait à n'en montrer que les premiers par ordre alphabétique.
   */
  onQueryChange?: (q: string) => void;
  /** Hint affiché à droite (ex: nom extrait par Claude si non matché). */
  hint?: string;
  disabled?: boolean;
}

/**
 * Combobox accessible : input texte + dropdown filtré. La recherche est
 * insensible à la casse, aux espaces, aux points et aux diacritiques
 * (cf. normalizeVendorName).
 */
export function VendorSelect({
  vendors,
  value,
  onChange,
  placeholder,
  hint,
  disabled,
  nomDeLObjet = 'fournisseur',
  onQueryChange,
}: Props) {
  const selected = useMemo(
    () => vendors.find((v) => v.id === value) ?? null,
    [vendors, value],
  );

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);

  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Quand on sélectionne, le query reflète le nom du vendor.
  useEffect(() => {
    if (!open) setQuery(selected?.name ?? '');
  }, [selected, open]);

  // Fermeture au clic extérieur.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery(selected?.name ?? '');
      }
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [open, selected]);

  const filtered = useMemo(() => searchVendors(query, vendors).slice(0, 50), [query, vendors]);

  useEffect(() => {
    setHighlight(0);
  }, [query]);

  const choose = (v: ZohoVendor | null) => {
    onChange(v?.id ?? null);
    setOpen(false);
    setQuery(v?.name ?? '');
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(filtered.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && filtered[highlight]) choose(filtered[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery(selected?.name ?? '');
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          disabled={disabled}
          value={open ? query : selected?.name ?? ''}
          onFocus={() => {
            setOpen(true);
            setQuery('');
          }}
          onChange={(e) => {
            setOpen(true);
            setQuery(e.target.value);
            onQueryChange?.(e.target.value);
          }}
          onKeyDown={onKeyDown}
          placeholder={placeholder ?? 'Rechercher un fournisseur Zoho…'}
          className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-400"
        />
        {selected && !disabled && (
          <button
            type="button"
            onClick={() => choose(null)}
            title="Effacer"
            className="text-slate-400 hover:text-slate-700 text-lg leading-none px-1"
          >
            ×
          </button>
        )}
      </div>

      {hint && !selected && (
        <p className="text-xs text-amber-700 mt-1 italic">{hint}</p>
      )}

      {open && !disabled && (
        <ul
          role="listbox"
          className="absolute z-30 mt-1 w-full max-h-72 overflow-auto rounded-md bg-white shadow-lg ring-1 ring-slate-200 text-sm"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-slate-400 italic">Aucun {nomDeLObjet} trouvé.</li>
          ) : (
            filtered.map((v, idx) => (
              <li
                key={v.id}
                role="option"
                aria-selected={value === v.id}
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(v);
                }}
                onMouseEnter={() => setHighlight(idx)}
                className={`px-3 py-1.5 cursor-pointer ${
                  idx === highlight ? 'bg-blue-50' : ''
                } ${value === v.id ? 'font-semibold' : ''}`}
              >
                <span>{v.name}</span>
                {v.company && v.company !== v.name && (
                  <span className="text-xs text-slate-500 ml-2">· {v.company}</span>
                )}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
